import { useMutation, useQuery } from '@tanstack/react-query'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'
import { ArgProps, GeneralDataSchema, GeneralResMock } from '../types/general.type'

type SpecialtyAttributes = {
  name: string
  created_at?: string
  updated_at?: string
}

type SpecialtySchema = GeneralDataSchema<SpecialtyAttributes>

type Props = ArgProps & {
  department_id?: string
}

export const useSpecialty = (arg?: Props) => {
  const { id, limit, page, query } = arg ?? {}
  const department_id = arg?.department_id
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<SpecialtySchema[]>>('specialties', {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(sort_by && { sort_by }),
          ...(sort_dir && { sort_dir }),
          ...(query && { query }),
          ...(department_id && { department_id })
        }
      })
      return data
    },
    queryKey: ['specialty', { department_id, limit, page, query, sort_by, sort_dir }]
  })

  const { data: singleSpecialty, isLoading: singleSpecialtyIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<SpecialtySchema>>(`specialties/${id}`)
      return data
    },
    queryKey: ['specialty', { id }]
  })

  const { mutateAsync: createSpecialty } = useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await AXIOS_INSTANCE.post('specialties', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialty'] })
      queryClient.invalidateQueries({ queryKey: ['config'] })
    }
  })

  const { mutateAsync: updateSpecialty } = useMutation({
    mutationFn: async ({ payload, id }: { payload: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`specialties/${id}`, payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['specialty'] })
      queryClient.invalidateQueries({ queryKey: ['specialty', { id }] })
      queryClient.invalidateQueries({ queryKey: ['config'] })
    }
  })

  const { mutateAsync: deleteSpecialty } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`specialties/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialty'] })
      queryClient.invalidateQueries({ queryKey: ['config'] })
    }
  })

  return {
    createSpecialty,
    data: data?.data,
    deleteSpecialty,
    include: data?.included,
    isLoading,
    pagination: data?.meta.pagination,
    singleSpecialty,
    singleSpecialtyIsLoading,
    updateSpecialty
  }
}

export const useSpeciaty = useSpecialty
